import JsApi from '@tencent/tvf-jsapi';
import { getCookie, getLoginInfo, handleError, handleSuccess, report } from '../helper';
import { ApiInterface, LoginInfo } from '../types';
import { Base } from './base';

/**
 * BBG
 */
export class BbgApi extends Base implements ApiInterface {
  public needRefresh = false; // 是否需要续期，外部可以根据此属性进行事件监听并在续期后处理逻辑
  public refreshed = false;
  public ifRefreshSuccess = false;

  /**
   * 是否在客户端内
   */
  private isInApp: boolean;

  public constructor({ isInApp }: { isInApp: boolean }) {
    super();
    this.isInApp = isInApp;
  }

  /**
   * 登录
   */
  @report('login')
  public async login() {
    // bbg没有端外场景
    if (!this.isInApp) return handleError(-1, 'BBG 暂不支持端外场景');

    // 打开 BBG 一定是先登录才能进 App，所以此方法进返回登录信息
    const { errCode, errMsg } = await JsApi.invoke('actionLogin');
    if (errCode !== 0) {
      return handleError(errCode, errMsg);
    }
    return handleSuccess(true);
  }

  /**
   * 退出登录
   */
  @report('logout')
  public async logout() {
    // bbg没有端外场景
    if (!this.isInApp) return handleError(-1, 'BBG暂不支持端外场景');

    // 未登录不允许调用
    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '当前未登录，无法退出登录');
    }

    const { errCode, errMsg } = await JsApi.invoke('logout');
    if (errCode !== 0) {
      return handleError(errCode, errMsg);
    }

    return handleSuccess(true);
  }

  /**
   * 是否登录
   */
  @report('isLogin')
  public async isLogin() {
    // 应用宝没有端外场景
    if (!this.isInApp) return handleError(-1, 'BBG暂不支持端外场景');

    const openid = getCookie('bbg_qq_openid') || getCookie('bbg_wx_openid');
    return handleSuccess(!!openid);
  }

  /**
   * 获取登录信息，cookie
   */
  @report('getLoginInfo')
  public async getLoginInfo() {
    // bbg没有端外场景
    if (!this.isInApp) return handleError(-1, 'BBG暂不支持端外场景');

    return getLoginInfo({
      isInApp: this.isInApp,
      loginInfoHandler: (_loginInfo: LoginInfo, appCookies: Record<string, string>) => {
        const loginInfo = _loginInfo;

        loginInfo.vuserid = getCookie('bbg_vuserid', appCookies);
        loginInfo.vusession = getCookie('bbg_vusession', appCookies);
        loginInfo.appid = getCookie('bbg_qq_appid', appCookies) || getCookie('bbg_wx_appid', appCookies);
        loginInfo.openid = getCookie('bbg_qq_openid', appCookies) || getCookie('bbg_wx_openid', appCookies);
        loginInfo.accessToken = getCookie('bbg_qq_access_token', appCookies) || getCookie('bbg_wx_access_token', appCookies);
        loginInfo.refreshToken = getCookie('bbg_qq_refresh_token', appCookies) || getCookie('bbg_wx_refresh_token', appCookies);
      },
    });
  }

  /**
   * 获取用户信息，头像昵称
   */
  @report('getUserInfo')
  public async getUserInfo() {
    // bbg没有端外场景
    if (!this.isInApp) return handleError(-1, 'BBG暂不支持端外场景');

    const userInfo = {
      nickname: '',
      avatar: '',
    };

    const { result: isLogin } = await this.isLogin();
    if (!isLogin) {
      return handleError(-1, '未登录，无法获取用户信息');
    }

    try {
      const { errCode, errMsg, result } = await JsApi.invoke('getMainUserInfo');
      if (errCode !== 0) {
        return handleError(errCode, errMsg);
      }

      userInfo.nickname = result?.userInfo?.nickname || '';
      userInfo.avatar = result?.userInfo?.headImgUrl || '';
    } catch (error) {
      return handleError(-1, '未登录，无法获取用户信息');
    }

    return handleSuccess(userInfo);
  }
}
